import { map } from 'lodash'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'

import { filterByRequireRules } from 'civic-champs-shared/utils/filterByRequireRules'

import { contactSort, ExpandableCell, NameCell } from 'core/table/cells/modern'
import { EditCell, EditHeader, FixedPointCell, PreFormattedCell } from 'core/table/cells'
import { DYNAMIC_NUMBER, DYNAMIC_TEXT } from 'core/table/filters'
import { createStringSort, createTagsSort } from 'core/table/utils'
import { createDateObjectSort } from 'components/table/sort'

import { useGroups } from 'group/hooks/useGroups'
import { useUISettings } from 'hooks/useUISettings'
import { getSmileyOptions, SatisfactionCell, SatisfactionHeader } from '../components'
import { useOrganizationRoles } from './useOrganizationRoles'
import { FIRST_LAST_NAME, GROUPS, REACTIONS, ROLES } from '../utils/filters'
import { useTimeZone } from '../../../civic-champs-shared/auth/hooks'
import { TAGS } from '../../../champion/utils/filters'
import { CellProps } from 'react-table'
import Tags from '../../../components/tag/Tags'
import { Tag } from '../../../new-mentorship/types'
import { useFeatureEnabled } from '../../../core/feature/hooks'
import { useTagsCollection } from '../../../tags/hooks'
import { getCheckInDateValue, getCheckInTimeValue, getCheckOutTimeValue } from '../utils/activityCheckInValues'

export const useStyles = makeStyles({
  link: {
    color: '#0F5DB5',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  selectColumn: {
    '&>div': {
      width: '46px',
    },
  },
  idColumn: {
    '&>div': {
      width: '70px',
    },
  },
  nameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  firstNameColumn: {
    '&>div': {
      width: '90px',
    },
  },
  lastNameColumn: {
    '&>div': {
      width: '110px',
    },
  },
  opportunityNameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  roleNameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  checkInColumn: {
    '&>div': {
      width: '90px',
    },
  },
  checkOutColumn: {
    '&>div': {
      width: '90px',
    },
  },
  dateColumn: {
    '&>div': {
      width: '100px',
    },
  },
  satisfactionColumn: {
    '&>div': {
      width: '80px',
    },
  },
  editColumn: {
    '&>div': {
      width: '40px',
    },
  },
  hoursColumn: {
    '&>div': {
      width: '140px',
    },
  },
  groupsColumn: {
    '&>div': {
      width: '310px',
    },
  },
})

export const useActivityLogColumns = (
  actions: {
    onEdit: (activity: { id: number }) => void
  },
  opportunityId?: number,
  { forProfile = false }: { forProfile?: boolean } = {},
) => {
  const classes = useStyles()
  // @ts-ignore
  const { showAdminItems } = useUISettings()
  const { roles } = useOrganizationRoles()
  const { groups } = useGroups()
  const timeZone = useTimeZone()
  const isVolunteerTagsEnabled = useFeatureEnabled('ActivityVolunteerTags')
  const [{ tags }] = useTagsCollection()

  return useMemo(
    () =>
      filterByRequireRules(
        [
          {
            id: 'id',
            Header: 'Id',
            accessor: 'id',
            disableFilters: true,
            requires: 'showAdminItems',
          },

          {
            id: 'name',
            Header: 'Name',
            accessor: 'user',
            Cell: ({ value: { id, givenName, familyName } }: any) => (
              <Link className={classes.link} to={`/champion/${id}`}>
                {givenName} {familyName}
              </Link>
            ),
            filter: FIRST_LAST_NAME,
            className: classes.nameColumn,
            sortType: contactSort,
            requires: 'notForProfile',
          },
          {
            id: 'firstName',
            Header: 'First Name',
            accessor: 'user.givenName',
            Cell: NameCell,
            filter: DYNAMIC_TEXT,
            className: classes.firstNameColumn,
            sortType: createStringSort('firstName'),
            requires: 'notForProfile',
          },
          {
            id: 'lastName',
            Header: 'Last Name',
            accessor: 'user.familyName',
            Cell: NameCell,
            filter: DYNAMIC_TEXT,
            className: classes.lastNameColumn,
            sortType: createStringSort('lastName'),
            requires: 'notForProfile',
          },
          {
            id: 'opportunity',
            Header: 'Opportunity',
            accessor: 'opportunity.name',
            className: classes.opportunityNameColumn,
            filter: DYNAMIC_TEXT,
            requires: 'noOpportunityId',
          },
          {
            id: 'role',
            Header: 'Role',
            accessor: 'volunteeringRole.name',
            className: classes.roleNameColumn,
            filter: ROLES,
            getFilterAutocomplete: (): string[] => map(roles, 'name').sort(),
          },
          {
            id: 'date',
            Header: 'Date',
            accessor: getCheckInDateValue(timeZone),
            Cell: PreFormattedCell,
            sortType: createDateObjectSort('date'),
            className: classes.dateColumn,
            disableFilters: true,
          },
          {
            id: 'hours',
            Header: 'Hours',
            accessor: 'hoursVolunteered',
            Cell: FixedPointCell,
            filter: DYNAMIC_NUMBER,
            sortType: 'basic',
          },
          {
            id: 'checkIn',
            Header: 'Check-In',
            accessor: getCheckInTimeValue(timeZone),
            Cell: PreFormattedCell,
            sortType: createDateObjectSort('checkIn'),
            className: classes.checkInColumn,
            disableFilters: true,
          },
          {
            id: 'checkOut',
            Header: 'Check-Out',
            accessor: getCheckOutTimeValue(timeZone),
            Cell: PreFormattedCell,
            sortType: createDateObjectSort('checkOut'),
            className: classes.checkOutColumn,
            disableFilters: true,
          },
          {
            id: 'reflections',
            Header: 'Reflections',
            accessor: 'reflection',
            Cell: ExpandableCell,
            className: classes.groupsColumn,
          },
          {
            id: 'satisfaction',
            Header: <SatisfactionHeader />,
            Title: 'Reactions',
            accessor: 'volunteerSatisfaction',
            Cell: SatisfactionCell,
            disableSortBy: true,
            filter: REACTIONS,
            getFilterAutocomplete: getSmileyOptions,
            className: classes.satisfactionColumn,
          },
          {
            id: 'groups',
            Header: 'Volunteer Groups',
            accessor: 'groups',
            Cell: ExpandableCell,
            filter: GROUPS,
            className: classes.groupsColumn,
            sortType: createStringSort('groups'),
            getFilterAutocomplete: (): any[] => map(groups, 'name'),
          },
          {
            Header: 'Volunteer Tags',
            accessor: 'volunteerTags',
            id: 'volunteerTags',
            filter: TAGS,
            requires: 'isVolunteerTagsEnabled',
            sortType: createTagsSort('volunteerTags'),
            Cell: ({ cell: { value } }: CellProps<any>) => (
              <Tags tags={(value || []).map(({ name: label, id, color }: Tag) => ({ label, id, color }))} />
            ),
            getFilterAutocomplete: () => tags.map(({ id, name, color }) => ({ label: name, value: id, color })),
            isTags: true,
          },
          {
            id: 'actions',
            Header: <EditHeader hideDelete />,
            Cell: ({ cell }: any) => <EditCell cell={cell} onEdit={actions.onEdit} />,
            disableSortBy: true,
            disableFilters: true,
            className: classes.editColumn,
          },
        ],
        {
          showAdminItems,
          noOpportunityId: !opportunityId,
          isVolunteerTagsEnabled,
          forProfile,
          notForProfile: !forProfile,
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions.onEdit, forProfile, roles.length, showAdminItems, isVolunteerTagsEnabled, tags, groups],
  )
}

export default useActivityLogColumns
